import { useState, useEffect } from 'react';
import { Avatar } from '@/types';
import { playNotAllowed } from '@/audio/notAllowed';
import './AvatarCard.css';

interface AvatarCardProps {
  avatar: Avatar;
  isSelected: boolean;
  isSelectedByCurrentPlayer: boolean;
  isAvailable: boolean;
  selectedByPlayerName?: string;
  selectedByUsername?: string;
  onSelect: (avatarId: string) => void;
  isLoading?: boolean;
}

export function AvatarCard({
  avatar,
  isSelected,
  isSelectedByCurrentPlayer,
  isAvailable,
  selectedByPlayerName,
  selectedByUsername,
  onSelect,
  isLoading = false,
}: AvatarCardProps) {
  const [iconUrl, setIconUrl] = useState<string>('');

  // Load icon from get_icon API (S3 via Lambda)
  useEffect(() => {
    const loadIcon = async () => {
      // Extract number from 'astronaut_01' -> '1' (remove leading zeros)
      const match = avatar.appearance.icon.match(/\d+/);
      const iconName = match 
        ? `player${parseInt(match[0], 10)}-8bit.png`
        : 'player1-8bit.png';
      
      try {
        const { apiClient } = await import('@/services/api');
        const dataUrl = await apiClient.getIcon(iconName);
        setIconUrl(dataUrl);
      } catch (error) {
        console.error(`Error loading avatar icon ${iconName}:`, error);
        // Fallback to empty string (will show broken image)
      }
    };
    
    loadIcon();
  }, [avatar.appearance.icon]);

  const handleClick = () => {
    if (!isAvailable) {
      playNotAllowed();
      return;
    }
    if (!isLoading) onSelect(avatar.avatarId);
  };

  const getCardClass = () => {
    if (isSelectedByCurrentPlayer) return 'avatar-card selected-by-you'; // Can be clicked to reselect
    if (isSelected) return 'avatar-card selected-by-other'; // Disabled - selected by another player
    if (isAvailable) return 'avatar-card available'; // Available for selection
    return 'avatar-card disabled'; // Should not happen with new logic
  };

  const getColorClass = () => {
    const colorMap: Record<string, string> = {
      cyan: 'color-cyan',
      magenta: 'color-magenta',
      yellow: 'color-yellow',
      green: 'color-green',
      orange: 'color-orange',
      purple: 'color-purple',
    };
    return colorMap[avatar.appearance.color] || 'color-cyan';
  };

  return (
    <div 
      className={`${getCardClass()} ${getColorClass()}`}
      onClick={handleClick}
    >
      {/* Avatar Color Indicator */}
      <div className="avatar-color-indicator" />

      {/* Avatar Name - only show if selected */}
      {selectedByPlayerName && (
        <div className="avatar-header">
          <div className="avatar-name">{selectedByPlayerName}</div>
          {selectedByUsername && (
            <div className="avatar-username">@{selectedByUsername}</div>
          )}
        </div>
      )}
      {!selectedByPlayerName && (
        <div className="avatar-name avatar-name-placeholder">Select Avatar</div>
      )}

      {/* Avatar Icon with Image */}
      <div className="avatar-icon">
        <div className={`avatar-icon-square ${getColorClass()}`}>
          {iconUrl && (
            <img 
              src={iconUrl} 
              alt={avatar.appearance.icon}
              className="avatar-image"
            />
          )}
        </div>
      </div>

      {/* Skills */}
      <div className="avatar-skills">
        <div className="skill-row">
          <span className="skill-label">Mining</span>
          <div className="skill-bar">
            <div 
              className="skill-bar-fill skill-mining"
              style={{ width: `${avatar.skills.mining}%` }}
            />
          </div>
          <span className="skill-value">{avatar.skills.mining}</span>
        </div>

        <div className="skill-row">
          <span className="skill-label">Farming</span>
          <div className="skill-bar">
            <div 
              className="skill-bar-fill skill-farming"
              style={{ width: `${avatar.skills.farming}%` }}
            />
          </div>
          <span className="skill-value">{avatar.skills.farming}</span>
        </div>

        <div className="skill-row">
          <span className="skill-label">Solar</span>
          <div className="skill-bar">
            <div 
              className="skill-bar-fill skill-solar"
              style={{ width: `${avatar.skills.solarFarming}%` }}
            />
          </div>
          <span className="skill-value">{avatar.skills.solarFarming}</span>
        </div>

        <div className="skill-row">
          <span className="skill-label">Chemist</span>
          <div className="skill-bar">
            <div 
              className="skill-bar-fill skill-chemist"
              style={{ width: `${avatar.skills.chemist}%` }}
            />
          </div>
          <span className="skill-value">{avatar.skills.chemist}</span>
        </div>

        <div className="skill-row">
          <span className="skill-label">Luck</span>
          <div className="skill-bar">
            <div 
              className="skill-bar-fill skill-luck"
              style={{ width: `${(avatar.skills.luck / 15) * 100}%` }}
            />
          </div>
          <span className="skill-value">{avatar.skills.luck}</span>
        </div>
      </div>

      {/* Selection Status - Only show loading state */}
      <div className="avatar-status">
        {isLoading && (
          <div className="status-badge status-loading">Selecting...</div>
        )}
      </div>
    </div>
  );
}

